import React, {ChangeEvent, FormEvent, ReactNode} from 'react';
import './LeadForm.scss';
import Input from '../../private/Input';
import Textarea from '../../private/Textarea';
import Title from '../../private/Title';
import Action from '../Action';
import PhoneCountryDropdown from '../PhoneCountryDropdown';
import { Country } from '../PhoneCountryDropdown/props';
import SellerName from '../SellerName';
import Image from '../../private/Image';
import SuccessMessage from '../SuccessMessage';
import ErrorMessageV2 from '../../private/ErrorMessageV2';
import Content from '../../private/Content';
import Icon from '../../private/Icon';
import errorIcon from '../../../icons/info_outline_red.svg';


export interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  phoneCountryPrefix: string;
  comments: string;
}

export interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  comments?: string;
}

export interface SellerInfo {
  name?: string;
  address?: string;
  phoneNumber?: string
  logo?: {
    src: string;
    alt: string;
  }
}
export interface LeadFormLabels {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  comments?: string;
  submitButton?: string;
}

export interface LeadFormProps {
  formData: FormData;
  errors?: FormErrors;
  sellerInfo: SellerInfo;
  onChange: (updatedForm: FormData) => void;
  onBlur?: (field: keyof FormData, value: string) => void;
  onSubmit: (formData: FormData) => void;
  countries?: Country[];
  sortCountries?: boolean,
  countrySelected: Country;
  showSuccess?: boolean;
  successMessage?: string;
  errorMessage?: string;
  showErrorMessage?: boolean;
  footer?: ReactNode;
  labels?: LeadFormLabels;
  ctaDisabled?: boolean;
  id?: string;
  isModalView?: boolean;
  modalTitle?: string;
}
const LeadForm = ({
  formData,
  onChange,
  onBlur,
  onSubmit,
  countries,
  sortCountries = true,
  countrySelected,
  errors = {},
  sellerInfo,
  showSuccess,
  successMessage = '',
  footer,
  labels,
  ctaDisabled = false,
  id,
  isModalView = false,
  modalTitle = 'Contact Seller',
  showErrorMessage = false,
  errorMessage = '',
}: LeadFormProps) => {

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onChange({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCountryChange = (countryPrefix: string) => {
    onChange({...formData, phoneCountryPrefix: countryPrefix});
  };

  const handleBlur = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onBlur?.(e.target.name as keyof FormData, e.target.value);
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSubmit(formData);
  };
  
  return (
    <div className={isModalView ? 'lead-form modal-lead-form' : 'lead-form'}>
      {isModalView && (
        <>
          <Title {...{text: modalTitle, classNames: { 'title-12': true }}} />
          <hr className="lead-form__horizontal-line" />
        </>
      )}
      {sellerInfo.logo?.src ?
        <div className="lead-form__image">
          <Image {...sellerInfo?.logo} /> 
        </div>
        :
        <SellerName linkContactInfo={false} classNames={{sellerNameV2: true}} name={sellerInfo?.name || ''} address={sellerInfo?.address || ''} phoneNumber={sellerInfo?.phoneNumber || ''}/>
      }
      {showErrorMessage && <ErrorMessageV2 title={errorMessage}  show={showErrorMessage} />}
      {showSuccess ? <SuccessMessage message={successMessage} classNames={{successCard: true, leadForm: true}} show={showSuccess} /> :
        <form className="lead-form__body" onSubmit={handleSubmit} aria-label={modalTitle}>
          <div className="lead-form__field">
          <Input
              id={`firstName__${id || ''}`}
              name="firstName"
              Label={{text: labels?.firstName || 'First Name'}}
              classNames={{labelMovesUp: true, errorLabelIconInline: true}}
              value={formData?.firstName || ''}
              onChange={handleChange}
              onBlur={handleBlur}
              invalid={!!errors.firstName}
              showErrorIcon={!!errors?.firstName}
              errorLabel={errors?.firstName}
            />  
          </div>      
          <div className="lead-form__field">
            <Input
              id={`lastName__${id || ''}`}
              name="lastName"
              Label={{text: labels?.lastName || 'Last Name'}}
              classNames={{labelMovesUp: true, errorLabelIconInline: true}}
              value={formData?.lastName || ''}
              onChange={handleChange}
              onBlur={handleBlur}
              invalid={!!errors.lastName}
              showErrorIcon={!!errors?.lastName}
              errorLabel={errors?.lastName}
            />
          </div>
          <div className="lead-form__field">
            <Input
              id={`email__${id || ''}`}
              name="email"
              Label={{text: labels?.email || 'Your Email'}}
              classNames={{labelMovesUp: true, errorLabelIconInline: true}}
              value={formData?.email || ''}
              onChange={handleChange}
              onBlur={handleBlur}
              invalid={!!errors.email}
              showErrorIcon={!!errors?.email}
              errorLabel={errors?.email}
            />
          </div>
          {countries && 
            <div className="lead-form__field">
              <div className='lead-form__field-phone'>
                <PhoneCountryDropdown
                    countrySelected = {countrySelected}
                    countries = {countries}
                    classNames={{error: !!errors?.phone}}
                    sortCountries={sortCountries}
                    onChangePrefix={handleCountryChange}
                />
                <Input
                id={`phone__${id || ''}`}
                name="phone"
                Label={{text: labels?.phone || 'Your Phone'}}
                classNames={{labelMovesUp: true, errorLabelIconInline: true}}
                value={formData?.phone || ''}
                onChange={handleChange}
                onBlur={handleBlur}
                invalid={!!errors.phone}
                />
              </div>
              {errors.phone && 
                <div className={"error-icon-label-inline"}>
                  <Icon svg={errorIcon} alt="error icon" classNames={{medium: true}}/> 
                    <div className="lead-form__error-message">
                      <Content text={errors.phone} classNames={{'content-2': true}} data-e2e={`error-message-phone`} />
                    </div>
                  </div>
              }
            </div> 
          }
          <div className="lead-form__field">
            <Textarea
              id={`comments__${id || ''}`}
              name="comments"
              labelText={labels?.comments || "Comments"}
              aria-required="true"
              classNames={{labelMovesUp: true, errorLabelIconInline: true}}
              value={formData?.comments}
              onChange={handleChange}
              onBlur={handleBlur}
              invalid={!!errors.comments}
              showErrorIcon={!!errors?.comments}
              errorLabel={errors?.comments}
            />
          </div>
          <div className='lead-form__footer'>
            <hr className="lead-form__horizontal-line" />
            <Action label="Contact Seller" e2e="lead-form-cta" stretch disabled={ctaDisabled} attributes={{ 'data-testing-class': id || '' }}/>
            {footer}
          </div>
        </form>
      }
    </div>
  )
}

export default React.memo(LeadForm);